import type { ApiErrorBody } from "./types";

// Thin fetch wrapper. credentials:'include' carries the httpOnly session cookie
// (sid) per API_CONTRACT.md's auth model — no JWT, no Authorization header.
const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

// timeoutMs is opt-in (not applied to every call by default) — a request
// that legitimately takes a while server-side (e.g. a big report) shouldn't
// get cut off just because SOME caller elsewhere in the app wants a bound.
async function request<T>(path: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const { timeoutMs, ...rest } = init ?? {};
  const controller = timeoutMs !== undefined ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(rest.body ? { "Content-Type": "application/json" } : {}),
        ...rest.headers,
      },
      signal: controller?.signal,
      ...rest,
    });
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as ApiErrorBody;
      if (body?.detail) detail = body.detail;
    } catch {
      // response wasn't JSON — keep statusText
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Distinguishes "the network/gateway glitched, try again" from "the server
// gave a real answer" — the compute245 bridge issue documented in
// docs/prod-environment.md sometimes drops the client's own TCP connect
// before it ever reaches nginx, which surfaces here as a plain network
// error (not an ApiError) or, when it does reach nginx/dashboard-api but a
// downstream hop is briefly wedged, as 502/503/504. Never retry a real
// answer (401 wrong password, 429 rate-limited, etc.) — that would just
// resubmit bad credentials and burn the rate limit for no reason.
export function isRetriableApiError(error: unknown): boolean {
  if (error instanceof ApiError) return [502, 503, 504].includes(error.status);
  return true; // fetch() itself threw (network error) or the timeout aborted it
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>, timeoutMs?: number) => {
    const qs = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    return request<T>(`${path}${qs}`, { method: "GET", timeoutMs });
  },
  post: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      timeoutMs,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }),
};

// GET /api/logs/download returns text/plain — bypasses the JSON request() helper.
export async function fetchLogsDownload(params: Record<string, string | boolean | undefined>): Promise<string> {
  const qs =
    "?" +
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");
  const res = await fetch(`${API_BASE}/logs/download${qs}`, { credentials: "include" });
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.text();
}
